import { DOCUMENT, Injectable, OnDestroy, Type, inject } from '@angular/core';
import {
    BehaviorSubject,
    Observable,
    ReplaySubject,
    Subject,
    from,
} from 'rxjs';
import { buffer, filter, map, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { ModalData } from '../interfaces/modal-data';

@Injectable({
    providedIn: 'root',
})
export class ModalService implements OnDestroy {
    private document = inject<Document>(DOCUMENT);

    active = new BehaviorSubject<boolean>(false);
    activeComponent: [Type<ModalData<any>>, any, boolean | Function, boolean];
    component = new ReplaySubject<
        [Type<ModalData<any>>, any, boolean | Function, boolean]
    >(1);
    previousSubject = new Subject<
        [Type<ModalData<any>>, any, boolean | Function, boolean]
    >();
    previousBuffer: Observable<
        [Type<ModalData<any>>, any, boolean | Function, boolean]
    >;
    backEvent = new Subject<boolean>();
    destroyService = new Subject();
    constructor() {
        this.component
            .pipe(takeUntil(this.destroyService))
            .subscribe(component => (this.activeComponent = component));

        this.previousBuffer = this.previousSubject.pipe(
            buffer(this.backEvent),
            filter(values => values.length >= 2),
            map(values => values.slice(0, -1)),
            tap(values => {
                const lastElement = values[values.length - 1];
                const backFunction = this.activeComponent[2];
                if (typeof backFunction === 'function') {
                    backFunction();
                }
                this.component.next(lastElement);
            }),
            mergeMap(values => from(values))
        );
        // fill the buffer immediately with the remaining elements
        this.previousBuffer.subscribe(this.previousSubject);
    }

    toggle() {
        this.active.next(!this.active.value);
    }

    show<T>(
        component: Type<ModalData<T>>,
        data?: T,
        back: boolean | Function = false,
        canHide = true
    ) {
        this.previousSubject.next([component, data, back, canHide]);
        this.component.next([component, data, back, canHide]);
        this.active.next(true);
        this.document.body.classList.add('modal-open');
    }

    ngOnDestroy(): void {
        this.hide();
        this.active.complete();
        this.previousSubject.complete();
        this.component.complete();
        this.destroyService.next(null);
        this.destroyService.complete();
    }

    hide() {
        this.active.next(false);
        this.backEvent.next(null);
        this.document.body.classList.remove('modal-open');
    }

    back() {
        this.backEvent.next(null);
    }
}
